import { Button, Text } from "@sundae/ui-toolkit";
import { FC, Suspense, lazy, useEffect } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { Dialogs } from "./Dialogs";
import Providers from "./Providers";
import ErrorFallback from "./components/ErrorFallback";
import LoadingComponent from "./components/LoadingComponent";
import "./i18n";
import MainLayout from "./layouts/MainLayout";
import Home from "./pages/Home";
import { NotFound } from "./pages/NotFound";
import TasteTestDetail from "./pages/TasteTestDetail";
import { TasteTestDetailContextProvider } from "./pages/TasteTestDetail/context";
import YieldFarming from "./pages/YieldFarming";
import { paths } from "./pages/routes";
import { OrderContextProvider } from "./stores/OrderContext";
import { WalletStrategyContextProvider } from "./stores/WalletStrategyContext";
import { checkIsEternlEnv } from "./utils/network.utils";
import { initCardanoDAppConnectorBridge } from "./vendor/cardano-dapp-connector-bridge";

const LazyExchange = lazy(() => import("./pages/Exchange"));
const LazyLiquidity = lazy(() => import("./pages/Liquidity"));
const LazyTasteTest = lazy(() => import("./pages/TasteTest"));

const FallbackComponent: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-2 p-2" role="alert">
      <Text tag="div" size="lg">
        Something went wrong:
      </Text>
      <Text tag="div">{error.message}</Text>
      <Button onClick={resetErrorBoundary} className="w-fit">
        Try again
      </Button>
    </div>
  );
};

export const App: FC = () => {
  /**
   * Effect that handles the cardano dApp bridge connection.
   */
  useEffect(() => {
    if (checkIsEternlEnv()) {
      initCardanoDAppConnectorBridge();
    }
  }, []);

  return (
    <WalletStrategyContextProvider>
      <ErrorBoundary
        FallbackComponent={(props) => (
          <ErrorBoundary FallbackComponent={FallbackComponent}>
            <ErrorFallback {...props} />
          </ErrorBoundary>
        )}
      >
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <Providers>
            <Routes>
              <Route path={paths.root} element={<MainLayout />}>
                <Route index element={<Home />} />
                <Route
                  path={paths.exchange}
                  element={
                    <Suspense fallback={<LoadingComponent className="min-h-[50vh]" />}>
                      <LazyExchange />
                    </Suspense>
                  }
                />
                <Route
                  path={paths.liquidity.root}
                  element={
                    <Suspense fallback={<LoadingComponent className="min-h-[50vh]" />}>
                      <LazyLiquidity />
                    </Suspense>
                  }
                />
                <Route path={paths.yieldFarming} element={<YieldFarming />} />
                <Route path="/taste-test">
                  <Route index element={<LazyTasteTest />} />
                  <Route
                    index={false}
                    path=":slug"
                    element={
                      <OrderContextProvider>
                        <TasteTestDetailContextProvider>
                          <TasteTestDetail />
                        </TasteTestDetailContextProvider>
                      </OrderContextProvider>
                    }
                  />
                </Route>

                {/* Using path="*"" means "match anything", so this route
                acts like a catch-all for URLs that we don't have explicit
                routes for. */}
                <Route path="*" element={<NotFound className="lg:min-h-[calc(100vh-254px)]" />} />
              </Route>
            </Routes>
            <Dialogs />
          </Providers>
        </BrowserRouter>
      </ErrorBoundary>
    </WalletStrategyContextProvider>
  );
};

export default App;
